import React from 'react';
import { Box, Stack, Typography, Tooltip } from '@mui/material';
import { NameEmailAvatar } from '../../../../../../ui';
import { AssessmentUserApiResponse } from '../Interfaces';
import { TableColumn } from '../Interfaces/TableColumn';

export const TooltipWrapper: React.FC<{ text: string }> = ({ text }) => {
  return (
    <Tooltip title={text || '-'}>
      <Typography
        variant="body2"
        noWrap
        sx={{ maxWidth: 180, display: 'inline-block' }}
      >
        {text || '-'}
      </Typography>
    </Tooltip>
  );
};

// Format time from seconds to HH:MM:SS or MM:SS format
const formatTime = (seconds: number | undefined): JSX.Element => {
  if (!seconds || seconds === 0) return <span>00:00</span>;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return (
      <span>{`${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds
        .toString()
        .padStart(2, '0')}`}</span>
    );
  }
  return (
    <span>{`${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`}</span>
  );
};

// Format timestamp to readable date
const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Calculate skill percentage
export const calculateSkillPercentage = (skill: any): number => {
  if (!skill || skill.totalCount === 0) return 0;
  return Math.round((skill.correctCount / skill.totalCount) * 100);
};

// Get candidate status badge color
export const getStatusColor = (status: string | undefined): string => {
  if (!status) return '#6b7280';

  switch (status.toLowerCase()) {
    case 'completed':
      return '#22c55e';
    case 'shortlisted':
      return '#3b82f6';
    case 'waitlisted':
      return '#eab308';
    case 'unsuccessful':
      return '#ef4444';
    default:
      return '#6b7280';
  }
};

// Generate columns based on assessment content
export const getColumns = (
  hasSkillQuestions: boolean = true,
  hasBEIQuestions: boolean = true,
): TableColumn<AssessmentUserApiResponse>[] => {
  const columns: TableColumn<AssessmentUserApiResponse>[] = [];

  // 1. Candidate column (always first)
  columns.push({
    key: '_id',
    label: 'Candidate',
    width: '220px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <NameEmailAvatar
        name={`${row.user.firstName} ${row.user.lastName}`}
        email={row.user.email}
        avatar=""
      />
    ),
    disableSort: false,
  });

  // 2. Skills Score (if has skill questions)
  if (hasSkillQuestions) {
    columns.push({
      key: 'score',
      label: 'Skills Score',
      width: '120px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => (
        <Typography variant="body2" color="text.secondary">
          {row.score}
        </Typography>
      ),
      disableSort: false,
    });
  }

  // 3. Skills % (if has skill questions)
  if (hasSkillQuestions) {
    columns.push({
      key: 'score',
      label: 'Skills %',
      width: '100px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => {
        const percentage =
          row.maxScore > 0 ? Math.round((row.score / row.maxScore) * 100) : 0;
        return (
          <Typography variant="body2" color="text.secondary">
            {percentage}%
          </Typography>
        );
      },
      disableSort: false,
    });
  }

  // 4. Behaviour Score (if has BEI questions)
  if (hasBEIQuestions) {
    columns.push({
      key: 'behavior_score',
      label: 'Behaviour %',
      width: '150px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => (
        <Typography variant="body2" color="text.secondary">
          {row.behaviorScore || 0}%
        </Typography>
      ),
      disableSort: false,
    });
  }

  // 5. Time Taken (if has skill questions)
  if (hasSkillQuestions) {
    columns.push({
      key: 'total_time',
      label: 'Time Taken',
      width: '120px',
      align: 'left' as const,
      render: (row: AssessmentUserApiResponse) => (
        <Stack alignItems="flex-start" spacing={0.25}>
          <Typography variant="body2" fontWeight={600}>
            {formatTime(row.totalTime)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total
          </Typography>
        </Stack>
      ),
      disableSort: false,
    });
  }

  // 6. Start Date
  columns.push({
    key: 'startDate',
    label: 'Start Date',
    width: '100px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <Typography variant="caption" fontWeight={500}>
        {formatDate(row.startTime)}
      </Typography>
    ),
    disableSort: false,
  });

  // 7. End Date
  columns.push({
    key: 'end_time',
    label: 'End Date',
    width: '100px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <Typography variant="caption" fontWeight={500}>
        {formatDate(row.endTime)}
      </Typography>
    ),
    disableSort: false,
  });

  // 8. Candidate Status
  columns.push({
    key: 'candidateStatus',
    label: 'Candidate Status',
    width: '120px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: getStatusColor(row.candidateStatus),
          }}
        />
        <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
          {row.candidateStatus}
        </Typography>
      </Stack>
    ),
    disableSort: true,
  });

  // 9. Assessment Status
  columns.push({
    key: 'status',
    label: 'Assessment Status',
    width: '140px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: getStatusColor(row.status),
          }}
        />
        <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
          {row.status}
        </Typography>
      </Stack>
    ),
    disableSort: true,
  });

  return columns;
};
